import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Layout } from './components/Layout.tsx'
import { Spinner } from './components/ui.tsx'
import { isStaff, useAuth } from './lib/auth.tsx'
import { ClientDashboard } from './pages/ClientDashboard.tsx'
import { ClientDetailPage } from './pages/ClientDetailPage.tsx'
import { ClientsPage } from './pages/ClientsPage.tsx'
import { GameCatalogPage } from './pages/GameCatalogPage.tsx'
import { LoginPage } from './pages/LoginPage.tsx'
import { PlayPage } from './pages/PlayPage.tsx'
import { ProgramEditPage } from './pages/ProgramEditPage.tsx'
import { ProgramsPage } from './pages/ProgramsPage.tsx'
import { ProgressPage } from './pages/ProgressPage.tsx'
import { SettingsPage } from './pages/SettingsPage.tsx'
import { StaffDashboard } from './pages/StaffDashboard.tsx'
import { UsersPage } from './pages/UsersPage.tsx'

export function App(): React.JSX.Element {
  const { user, loading } = useAuth()

  // Dokud se neověří cookie, nelze rozhodnout, kam uživatele pustit.
  if (loading) return <Spinner label="Ověřuji přihlášení…" />

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/* Trénink běží na celé obrazovce, mimo běžné rozvržení. */}
      <Route path="/hra/:assignmentId" element={<PlayPage />} />
      <Route path="/hra/program/:programId" element={<PlayPage byProgram />} />

      <Route element={<Layout />}>
        <Route index element={isStaff(user) ? <StaffDashboard /> : <ClientDashboard />} />
        <Route path="/pokrok/:gameId" element={<ProgressPage />} />
        <Route path="/nastaveni" element={<SettingsPage />} />

        <Route path="/klienti" element={<StaffOnly><ClientsPage /></StaffOnly>} />
        <Route path="/klienti/:id" element={<StaffOnly><ClientDetailPage /></StaffOnly>} />
        <Route path="/programy" element={<StaffOnly><ProgramsPage /></StaffOnly>} />
        <Route path="/programy/novy" element={<StaffOnly><ProgramEditPage /></StaffOnly>} />
        <Route path="/programy/:id" element={<StaffOnly><ProgramEditPage /></StaffOnly>} />
        <Route path="/trenink" element={<StaffOnly><GameCatalogPage /></StaffOnly>} />
        <Route path="/uzivatele" element={<AdminOnly><UsersPage /></AdminOnly>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

/** Po přihlášení vrátí uživatele tam, kam původně mířil. */
function RedirectToLogin(): React.JSX.Element {
  const location = useLocation()
  return <Navigate to="/login" replace state={{ from: location.pathname }} />
}

function StaffOnly({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { user } = useAuth()
  return isStaff(user) ? children : <Navigate to="/" replace />
}

function AdminOnly({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { user } = useAuth()
  return user?.role === 'admin' ? children : <Navigate to="/" replace />
}
